import {
  CATEGORY_GRAINS,
  getCategoryAncestry,
  getCategoryLabelType,
} from '../constants/filterCategories';

export const TAXONOMY_GROUP_GRAINS = {
  ALL_SOFTWARE: 'allSoftware',
  SEGMENT: 'segment',
  INDUSTRY: CATEGORY_GRAINS.INDUSTRY,
  SUB_INDUSTRY: CATEGORY_GRAINS.SUB_INDUSTRY,
  CATEGORY: CATEGORY_GRAINS.CATEGORY,
  MARKET: CATEGORY_GRAINS.MARKET,
};

const DEFAULT_FALLBACK = 'Uncategorized';

const GRAIN_ALIASES = {
  allsoftware: TAXONOMY_GROUP_GRAINS.ALL_SOFTWARE,
  all_software: TAXONOMY_GROUP_GRAINS.ALL_SOFTWARE,
  allSoftware: TAXONOMY_GROUP_GRAINS.ALL_SOFTWARE,
  segment: TAXONOMY_GROUP_GRAINS.SEGMENT,
  industry: TAXONOMY_GROUP_GRAINS.INDUSTRY,
  industries: TAXONOMY_GROUP_GRAINS.INDUSTRY,
  subindustry: TAXONOMY_GROUP_GRAINS.SUB_INDUSTRY,
  subIndustry: TAXONOMY_GROUP_GRAINS.SUB_INDUSTRY,
  sub_industry: TAXONOMY_GROUP_GRAINS.SUB_INDUSTRY,
  subcategory: TAXONOMY_GROUP_GRAINS.MARKET,
  subCategory: TAXONOMY_GROUP_GRAINS.MARKET,
  category: TAXONOMY_GROUP_GRAINS.CATEGORY,
  Category: TAXONOMY_GROUP_GRAINS.CATEGORY,
  market: TAXONOMY_GROUP_GRAINS.MARKET,
  Market: TAXONOMY_GROUP_GRAINS.MARKET,
};

const VALUE_FIELDS = {
  market: ['market', 'Market'],
  category: ['categoryGroup', 'category', 'Category'],
  subIndustry: ['subIndustry', 'Sub Industry', 'sub_industry'],
  industry: ['industry', 'Industry'],
};

const readFirst = (source, fields) => {
  if (!source || typeof source !== 'object') return null;
  for (const field of fields) {
    const value = source[field];
    if (value !== null && value !== undefined && String(value).trim()) {
      return String(value).trim();
    }
  }
  return null;
};

const getCandidateValues = (source, extraFields = []) => {
  if (source == null) return [];
  if (typeof source === 'string') return [source];
  if (typeof source !== 'object') return [];

  return [
    ...extraFields,
    ...VALUE_FIELDS.market,
    ...VALUE_FIELDS.category,
    ...VALUE_FIELDS.subIndustry,
    ...VALUE_FIELDS.industry,
  ]
    .map((field) => source[field])
    .filter((value) => value !== null && value !== undefined && String(value).trim())
    .map((value) => String(value).trim());
};

export const normalizeTaxonomyGroupGrain = (grain = TAXONOMY_GROUP_GRAINS.CATEGORY) => {
  const key = String(grain || '').trim();
  return GRAIN_ALIASES[key] || GRAIN_ALIASES[key.toLowerCase()] || TAXONOMY_GROUP_GRAINS.CATEGORY;
};

export const getCanonicalTaxonomyAncestry = (source, { candidateFields = [] } = {}) => {
  const candidates = getCandidateValues(source, candidateFields);
  for (const candidate of candidates) {
    const ancestry = getCategoryAncestry(candidate);
    if (ancestry) return ancestry;
  }
  return null;
};

export const getTaxonomySegmentLabel = (source, fallback = DEFAULT_FALLBACK) => {
  const ancestry = getCanonicalTaxonomyAncestry(source);
  if (!ancestry) {
    if (typeof source === 'string' && source.trim()) return source.trim();
    return readFirst(source, VALUE_FIELDS.subIndustry)
      || readFirst(source, VALUE_FIELDS.category)
      || readFirst(source, VALUE_FIELDS.market)
      || fallback;
  }

  if (ancestry.industry === 'Payments & FinTech') return 'Payments & FinTech';
  if (ancestry.industry === 'Information Services') return 'Information Services';
  if (ancestry.subIndustry === 'Application Software') return 'Application Software';
  if (ancestry.subIndustry === 'Infrastructure Software') return 'Infrastructure Software';
  if (ancestry.subIndustry === 'Vertical Software') return 'Vertical Software';
  return ancestry.subIndustry || ancestry.industry || fallback;
};

export const getTaxonomyGroupLabel = (
  source,
  grain = TAXONOMY_GROUP_GRAINS.CATEGORY,
  {
    fallback = DEFAULT_FALLBACK,
    candidateFields = [],
  } = {}
) => {
  const normalizedGrain = normalizeTaxonomyGroupGrain(grain);
  if (normalizedGrain === TAXONOMY_GROUP_GRAINS.ALL_SOFTWARE) return 'All Software';
  if (normalizedGrain === TAXONOMY_GROUP_GRAINS.SEGMENT) return getTaxonomySegmentLabel(source, fallback);

  const ancestry = getCanonicalTaxonomyAncestry(source, { candidateFields });
  if (ancestry) {
    if (normalizedGrain === TAXONOMY_GROUP_GRAINS.INDUSTRY) return ancestry.industry;
    if (normalizedGrain === TAXONOMY_GROUP_GRAINS.SUB_INDUSTRY) return ancestry.subIndustry;
    if (normalizedGrain === TAXONOMY_GROUP_GRAINS.MARKET) return ancestry.market;
    return ancestry.category;
  }

  if (typeof source === 'string') {
    const label = source.trim();
    if (!label) return fallback;
    const type = getCategoryLabelType(label);
    if (type === normalizedGrain) return label;
    return label;
  }

  if (normalizedGrain === TAXONOMY_GROUP_GRAINS.INDUSTRY) {
    return readFirst(source, VALUE_FIELDS.industry)
      || readFirst(source, VALUE_FIELDS.subIndustry)
      || readFirst(source, VALUE_FIELDS.category)
      || fallback;
  }

  if (normalizedGrain === TAXONOMY_GROUP_GRAINS.SUB_INDUSTRY) {
    return readFirst(source, VALUE_FIELDS.subIndustry)
      || readFirst(source, VALUE_FIELDS.category)
      || readFirst(source, VALUE_FIELDS.market)
      || fallback;
  }

  if (normalizedGrain === TAXONOMY_GROUP_GRAINS.MARKET) {
    return readFirst(source, VALUE_FIELDS.market)
      || readFirst(source, VALUE_FIELDS.category)
      || fallback;
  }

  return readFirst(source, VALUE_FIELDS.category)
    || readFirst(source, VALUE_FIELDS.market)
    || fallback;
};

export const getTaxonomyStackKey = (
  source,
  stackBy = TAXONOMY_GROUP_GRAINS.CATEGORY,
  options = {}
) => getTaxonomyGroupLabel(source, stackBy, options);
